import { useCallback, useEffect, useMemo, useState } from 'react';

import { CloseFullscreen, OpenInFull } from '@mui/icons-material';
import { Box, IconButton, Paper, Tooltip } from '@mui/material';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import ExportChartButton from '@/components/Chart/ExportButton/ExportChartButton';
import ExportCsvButton from '@/components/Chart/ExportButton/ExportCsvButton';
import { MarketWidget } from '@/components/MarketWidget';
import TabPanel from '@/components/TabPanel';
import Tabs from '@/components/Tabs';
import { PERSONALIZATION_FLAG } from '@/constants/featureFlags';
import useIsPremiumAccess from '@/hooks/useIsPremiumAccess';
import { useBlockTabNavigation } from '@/pages/Markets/hooks/useBlockTabNavigation';
import { useMarketAppControls } from '@/pages/Markets/MarketsComponents/contexts/MarketAppControlsContext';
import { useMarketWidget } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import { ShareBlock, ShareBlockNames } from '@/pages/Markets/MarketsComponents/Share/ShareBlock';
import { getMarketBlockTabParam } from '@/pages/Markets/utils/getMarketBlockTabParam';
import type { BaseTabType } from '@/types/markets';

import AddToDashboardButton from '../AddToDashboardButton';

type TabsContainerProps = {
  tabs: BaseTabType[];
  layout?: { fullWidth?: boolean; height?: number };
  blockIndex: number;
};

type TooltipIconButtonProps = {
  title: string;
  onClick: () => void;
  icon: React.ReactNode;
  id?: string;
};

const TooltipIconButton = ({ title, onClick, icon, id }: TooltipIconButtonProps) => {
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  return (
    <Tooltip
      open={open}
      slotProps={{
        popper: {
          sx: { zIndex: 9999 }
        }
      }}
      title={title}
      onClose={handleClose}
      onOpen={handleOpen}
    >
      <IconButton
        id={id}
        sx={{
          width: 30,
          height: 48,
          p: 0,
          minWidth: 30,
          borderRadius: 0,
          backgroundColor: 'secondary.main'
        }}
        onClick={() => {
          handleClose();
          onClick();
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};
const TabsContainer = ({ tabs = [], layout, blockIndex }: TabsContainerProps) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const { showShareBlock, showAddToDashboard } = useMarketAppControls();
  const { fullWidth } = layout ?? {};

  const { isFullScreen, setIsFullScreen, setFullScreenAvailable, fullScreenAvailable } =
    useMarketWidget();

  const isPremiumAccessGranted = useIsPremiumAccess();

  const isPersonalizationEnabled = useFeatureFlagEnabled(PERSONALIZATION_FLAG);

  // Local editable tabs order for edit mode
  const visibleTabs = tabs;
  const blockName: ShareBlockNames = useMemo(() => {
    const tab = visibleTabs[activeTab];
    switch (tab.type) {
      case 'table':
        return ShareBlockNames.Table;
      case 'graph':
        if (tab?.metadata?.graphType === 'custom') {
          return ShareBlockNames.CustomChart;
        }
        if (tab?.metadata?.graphType === 'seasonal') {
          return ShareBlockNames.SeasonalChart;
        }
        if (tab?.metadata?.graphType === 'forwardSimple') {
          return ShareBlockNames.ForwardCurveChart;
        }
        return ShareBlockNames.SpotChart;
      case 'feed':
        return ShareBlockNames.Feed;
      case 'minitable':
        return ShareBlockNames.Tables;
      case 'csvtable':
        return ShareBlockNames.CSVTable;
      case 'file_list':
        return ShareBlockNames.Files;
      default:
        return ShareBlockNames.Table;
    }
  }, [activeTab, visibleTabs]);

  const isFullscreenAvailable = useCallback(
    (tabIndex: number) => {
      const tab = visibleTabs[tabIndex];
      let available = false;
      if (tab?.type === 'graph') {
        const graphType = tab?.metadata?.graphType;
        // Only dynamic/spot charts should allow fullscreen
        available = !['custom', 'seasonal', 'forwardSimple'].includes(graphType as string);
      }
      return available;
    },
    [visibleTabs]
  );

  // Update full screen availability as a side-effect based on the current tab
  useEffect(() => {
    setFullScreenAvailable(isFullscreenAvailable(activeTab));
  }, [activeTab, visibleTabs, setFullScreenAvailable, isFullscreenAvailable]);

  const shareTabParam = useMemo(
    () => getMarketBlockTabParam(blockName, blockIndex, visibleTabs[activeTab].name),
    [blockIndex, blockName, activeTab, visibleTabs]
  );

  const tabsWithDisabled = useMemo(
    () =>
      tabs.map((tab, index) => ({
        ...tab,
        disabled: isFullScreen && !isFullscreenAvailable(index)
      })),
    [tabs, isFullScreen, isFullscreenAvailable]
  );

  const { blockRef } = useBlockTabNavigation(visibleTabs, setActiveTab);

  return (
    <Box
      ref={blockRef}
      component={Paper}
      sx={{
        ...(fullWidth ? { gridColumn: 'span 2' } : {}),
        ...(isFullScreen
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              zIndex: 9998,
              display: 'flex',
              flexDirection: 'column',
              // Allow inner panel to handle its own scrolling
              overflow: 'hidden',
              py: 2
            }
          : {})
      }}
    >
      <Box
        className='tabs__container'
        sx={{ height: 48, mb: 1.8, flexShrink: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            ...(isFullScreen && { px: 2 })
          }}
        >
          <Tabs
            activeTab={activeTab}
            height={48}
            scrollButtons='auto'
            tabs={tabsWithDisabled}
            onChange={setActiveTab}
          />
          <Box
            sx={{
              display: 'flex',
              gap: '2px',
              alignItems: 'center'
            }}
          >
            {tabs[activeTab]?.type !== 'csvtable' &&
              tabs[activeTab]?.metadata?.graphType !== 'custom' &&
              isPersonalizationEnabled &&
              showAddToDashboard && <AddToDashboardButton tab={tabs[activeTab]} />}
            {showShareBlock && (
              <ShareBlock
                activeTabName={tabs[activeTab].name}
                blockName={blockName}
                shareTabParam={shareTabParam}
              />
            )}
            {isPremiumAccessGranted && tabs[activeTab]?.type === 'graph' && (
              <ExportChartButton
                exportCsv={tabs[activeTab]?.metadata?.graphType === 'custom'}
                tabName={tabs[activeTab].name}
              />
            )}
            {isPremiumAccessGranted && tabs[activeTab]?.type === 'csvtable' && (
              <ExportCsvButton tabName={tabs[activeTab].name} />
            )}
            {isPremiumAccessGranted &&
              fullScreenAvailable &&
              (isFullScreen ? (
                <TooltipIconButton
                  icon={<CloseFullscreen sx={{ fontSize: 14 }} />}
                  title='Close full screen'
                  onClick={() => {
                    setIsFullScreen(prev => !prev);
                  }}
                />
              ) : (
                <TooltipIconButton
                  icon={<OpenInFull sx={{ fontSize: 14 }} />}
                  id='fs-button'
                  title='Full screen'
                  onClick={() => {
                    setIsFullScreen(prev => !prev);
                  }}
                />
              ))}
          </Box>
        </Box>
      </Box>
      {visibleTabs.map((tab, index) => (
        <TabPanel
          key={tab.id ?? tab.name}
          containerSX={{
            ...(isFullScreen
              ? {
                  // occupy remaining space below the tabs header and respect parent padding
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  px: 2
                }
              : {})
          }}
          index={index}
          value={activeTab}
        >
          <MarketWidget tab={tab} />
        </TabPanel>
      ))}
    </Box>
  );
};
export default TabsContainer;
